import React, { useEffect, useMemo, useState } from 'react';
import type { Key } from 'react';
import type { RouteComponentProps } from 'react-router-dom';

import toast from 'react-hot-toast';
import moment from 'moment';

import { ColumnDef, SortingState } from '@tanstack/react-table';

import { Divider } from 'primereact/divider';
import { Button as PrimeButton } from 'primereact/button';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWalking } from '@fortawesome/pro-solid-svg-icons';
import { faTrash } from '@fortawesome/pro-light-svg-icons';

import Table from 'Components/Table';
import type {
  PanelRender,
  TablePaginationConfig,
  TableRowSelection,
  TableSortConfig,
} from 'Components/Table/types';

import { Button, FlexBox } from '@eltoro-ui/components';
import { ConfirmationModal, EmptyStatus, PageHeader, ReactTableSearchBar } from 'Components';
import { Tags } from 'Pages/SavedAudiences/components';
import AudienceStatusAccessor from 'Pages/SavedAudiences/AudienceStatusAccessor';

import { deleteAudience, getAudiences } from 'Requests/Request_Methods/audienceMethods';
import { usePermissionContext } from 'contexts/PermissionContext';

import type { TAudience, TResPagination } from 'types';
import { AudienceStatusEnum } from 'enums';

import './SavedAudiences.scss';

export const SavedAudiences = ({ location }: RouteComponentProps) => {
  const { userIsDeactivatedWithActiveOrderlines } = usePermissionContext();
  const [audiences, setAudiences] = useState<TAudience[]>([]);
  const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false);
  const [deleteIsLoading, setDeleteIsLoading] = useState(false);
  const { userIsActive } = usePermissionContext();

  const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);
  const [, setSelectedAudiences] = useState<TAudience[]>([]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [page, setPage] = useState<number>(1);
  const [pagination, setPagination] = useState<TResPagination>({ total_count: 0, total_pages: 0 });
  const [searchValue, setSearchValue] = useState('');
  // ========== New Table Utilities
  const [fetchDataIsLoading, setFetchDataIsLoading] = useState(false);

  const handleSearchValue = (value: string) => {
    setSorting([]);
    setSearchValue(value);
    setPage(1);
  };
  const onDelete = async (ids: Key[]) => {
    setDeleteIsLoading(true);
    try {
      const { data } = await deleteAudience({ ids });
      if (data?.msg) toast.success(data.msg);
      setDeleteModalIsOpen(false);
      setDeleteIsLoading(false);

      setSelectedRowKeys([]);
      setSelectedAudiences([]);
      if (page === 1) _fetchData();
      else await setPage(1);
    } catch (err: any) {
      toast.error(err.detail);
      setDeleteIsLoading(false);
    }
  };

  const _fetchData = async () => {
    setFetchDataIsLoading(true);
    try {
      const [sort] = sorting;
      let sort_key: string | null = null;
      let order: string | null = null;

      if (sort) {
        sort_key = sort.id;
        order = sort.desc ? 'desc' : 'asc';
      }

      const res = await getAudiences(page, 50, searchValue, sort_key, order);
      if (res.data?.audiences) {
        setAudiences(res.data?.audiences);
        const { total_audience, total_pages } = res.data;
        setPagination({ total_count: total_audience, total_pages });
      }
      setFetchDataIsLoading(false);
    } catch (err: any) {
      toast.error(err.detail);
      setFetchDataIsLoading(false);
    }
  };

  useEffect(() => {
    if (userIsActive) {
      _fetchData();
    }
  }, [page, searchValue, sorting?.[0]]);

  const columns: ColumnDef<TAudience>[] = [
    {
      header: 'Audience Name',
      accessorKey: 'name',
    },
    {
      header: 'Prospects',
      accessorKey: 'prospects_counts',
      cell: ({ row: { original: audience } }) => (
        <span
          style={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <FontAwesomeIcon
            icon={faWalking}
            color={
              audience.status !== AudienceStatusEnum.ACTIVE
                ? '#B0B0B0'
                : 'var(--color-secondary-300)'
            }
            style={{ marginRight: '5px' }}
          />
          {audience.prospects_counts || '-'}
        </span>
      ),
    },
    {
      header: 'Status',
      accessorKey: 'status',
      enableSorting: false,
      cell: ({ row: { original: audience } }) =>
        <AudienceStatusAccessor status={audience.status} /> || '-',
    },
    {
      header: 'Source',
      accessorKey: 'source',
    },
    {
      header: 'Segment',
      accessorKey: 'segment',
      accessorFn: audience => audience.segment || '-',
    },
    {
      header: 'Date Uploaded',
      accessorKey: 'created_at',
      accessorFn: audience => moment(`${audience.created_at}Z`).format('MM/DD/YYYY hh:mm A'),
    },
    {
      header: 'Tags',
      accessorKey: 'tags',
      enableSorting: false,
      cell: ({ row: { original: audience } }) =>
        audience.tags?.length ? (
          <span className="SavedAudiences__tagsContainer">
            <Tags data={audience.tags} />
          </span>
        ) : (
          '-'
        ),
    },
  ];

  const rowSelection = useMemo<TableRowSelection<TAudience>>(
    () => ({
      selectedRowKeys,
      onChange: (selectedRowKeys, selectedRows) => {
        setSelectedRowKeys(selectedRowKeys);
        setSelectedAudiences(prevAudiences => {
          const filteredRows = prevAudiences.filter(
            row => audiences.findIndex(({ id }) => id === row.id) === -1
          );
          return [...filteredRows, ...selectedRows];
        });
      },
    }),
    [selectedRowKeys, audiences]
  );

  const tableTitle: PanelRender<TAudience> | undefined = selectedRowKeys.length
    ? () => {
        return (
          <FlexBox alignItems="center" gap="0.75rem">
            <span className="selected-items-count">
              {`${selectedRowKeys.length} contact${selectedRowKeys.length > 1 ? 's' : ''} selected`}
            </span>
            <Divider data-pr-classname="table-header-divider" layout="vertical" />
            <PrimeButton
              text
              severity="danger"
              className="table-delete-btn"
              icon={<FontAwesomeIcon icon={faTrash} />}
              onClick={() => setDeleteModalIsOpen(true)}
            />
          </FlexBox>
        );
      }
    : undefined;

  const tablePagination = useMemo<TablePaginationConfig>(
    () => ({
      hideOnSinglePage: true,
      pageSize: 50,
      current: page,
      total: pagination.total_count,
      totalPages: pagination.total_pages,
      onChange: page => setPage(page),
    }),
    [page, pagination]
  );

  const sortConfig: TableSortConfig = {
    sorting,
    onSortingChange: setSorting,
  };

  const newCampaignUrl = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.append('action', 'create-campaign');

    return `${location.pathname}?${searchParams.toString()}`;
  }, [location]);

  return (
    <div className="SavedAudiences">
      <PageHeader
        UNSAFE_CLASSNAME="SavedAudiences__PageHeader"
        LeftContent_ClassName="MyListings__header"
        subTitle={
          <span style={{ marginLeft: '5px' }} className="MyListings__totalCounts">
            You have <b>{pagination.total_count} Saved Audience</b>
          </span>
        }
        actions={[
          <ReactTableSearchBar
            key="search-audiences"
            searchValue={searchValue}
            handleSearchValue={handleSearchValue}
            placeholder="Search by audience name"
          />,
          <Button
            key="new-campaign"
            kind="primary"
            to={newCampaignUrl}
            replace
            weight="bolder"
            size="l"
            disabled={userIsDeactivatedWithActiveOrderlines}
          >
            New Campaign
          </Button>,
        ]}
      />
      <Table<TAudience>
        rounded={false}
        columns={columns}
        data={audiences}
        loading={fetchDataIsLoading}
        title={tableTitle}
        rowSelection={rowSelection}
        rowDisabled={audience => audience.status !== AudienceStatusEnum.ACTIVE}
        sortConfig={sortConfig}
        pagination={tablePagination}
        emptyText={
          <EmptyStatus
            heading={searchValue && !audiences.length ? 'No results found' : undefined}
            subHeading={
              searchValue && !audiences.length
                ? 'You might consider trying different keywords, double-checking for any spelling errors, or adjusting your filters.'
                : 'You can try creating a new campaign to get a better audience.'
            }
          />
        }
      />
      {deleteModalIsOpen && (
        <ConfirmationModal
          heading="Are you sure you want to delete the selected audience(s)?"
          note="The audiences attached to campaign(s) will not be deleted"
          onSubmit={() => onDelete(selectedRowKeys)}
          onCancel={() => setDeleteModalIsOpen(false)}
          loading={deleteIsLoading}
          okText="Yes, Delete"
          cancelText="No, Thanks"
          deletion
        />
      )}
    </div>
  );
};
